import React from 'react';
import styles from './organizations_list.module.scss';
import { Organization } from '../../../types/organization.types';
import ErrorBoundary from '../../../components/react/error_boundary/error_boundary';
import {
  EVENT_NAME_PROP,
  ORGS_SECTION_PROP,
  VIEW_SINGLE_RESULT_INTENTION,
} from '../../constants/global_search.constants';
import { ORGANIZATIONS_PATH } from '../../../utilities/devex_paths';
import classNames from 'classnames';

interface Props {
  results: Organization[];
  isFetching: boolean;
}

const CLOUDINARY_BASE_URL =
  'https://res.cloudinary.com/devex/image/fetch/c_pad,f_auto,q_auto,h_100,w_100';
const TRACKING = [EVENT_NAME_PROP, VIEW_SINGLE_RESULT_INTENTION, ORGS_SECTION_PROP].join(' ');

const OrganizationsList: React.FC<Props> = ({ results, isFetching }) => {
  const renderSingleOrgPlaceholder = (
    <>
      <div className='col-md-3 col-lg-2 d-none d-md-block'>
        <div className='placeholder-glow'>
          <p className={`placeholder w-100 ${styles.imgPlaceholder}`}></p>
        </div>
      </div>
      <div className='col-12 col-lg-10 col-md-9 mt-25'>
        <div className='placeholder-glow'>
          <p className='placeholder w-50 d-block' />
          <p className='placeholder w-50 d-block' />
          <p className='placeholder w-25 d-block' />
        </div>
      </div>
    </>
  );
  const renderLoadingPlaceholder = (
    <div data-testid='loading-placeholder' className='row pt-35'>
      {renderSingleOrgPlaceholder}
      {renderSingleOrgPlaceholder}
      {renderSingleOrgPlaceholder}
    </div>
  );

  const renderResults = (
    <div data-testid='organizations-list'>
      {results.slice(0, 3).map((org) => (
        <ErrorBoundary key={org.id}>
          <div className='row py-35 border-bottom'>
            <div className='col-md-3 col-lg-2 d-none d-md-block'>
              <div className='border py-4 px-2 text-center'>
                {org.logo_url ? (
                  <img
                    data-testid='organizationLogo'
                    alt='Organization logo'
                    src={`${CLOUDINARY_BASE_URL}/${org.logo_url}`}
                  />
                ) : (
                  <div className='text-gray-light my-25 fs-2 lh-1'>
                    <i className='fal icon-building' />
                  </div>
                )}
              </div>
            </div>
            <div className='col-12 col-lg-10 col-md-9 mt-25'>
              <a
                className={classNames('link-blue-orange', TRACKING)}
                href={`${ORGANIZATIONS_PATH}/${org.slug_and_id}`}
              >
                <h2 className='fs-6 fw-bold'>{org.name}</h2>
              </a>
              {Boolean(org.countries_with_offices?.length) && (
                <div className='d-none d-md-block'>
                  <small className='text-gray-li'>
                    Offices in{' '}
                    {org.countries_with_offices
                      .slice(0, 4)
                      .map((office) => office.name)
                      .join(', ')}
                    {org.countries_with_offices.length > 4 ? ' and more' : ''}
                  </small>
                </div>
              )}
              <div className='mt-15 mt-md-35'>
                {org.organization_types.join(', ')}
                {org.organization_size !== 'Unknown' &&
                  Boolean(org.organization_types.length) &&
                  `, Staff ${org.organization_size}`}
              </div>
            </div>
          </div>
        </ErrorBoundary>
      ))}
    </div>
  );

  return isFetching ? renderLoadingPlaceholder : renderResults;
};

export default OrganizationsList;
